import React from 'react'
import Link from '@mui/material/Link'
import { useData, useHandlers } from './hooks'
import { ENTITY_TYPES } from '@sales-center/common'
import Box from '@mui/material/Box'
import { useMapViewFeatureEnabled } from '@user/store/tenantFeatures/hooks/useMapViewFeatureEnabled'
import { MapAddressIcon } from './MapAddressIcon'
import { useTheme } from '@mui/material/styles'
import InfoOutlined from '@mui/icons-material/InfoOutlined'
import { useCheckPermissions } from '@user/store/authorization/permissions'
import { LightTooltip } from '@sales-center/components'
import { getStyles } from './styles'
import { TypographyProps } from '@mui/material/Typography'

type AddressMapLinkProps = {
  address: string
  children?: React.ReactNode
  entityId: number
  entityType?: ENTITY_TYPES
  serviceLocationId?: number
  latitude?: number | null
  longitude?: number | null
  variant?: 'plain' | 'pin-and-title'
  textVariant?: TypographyProps['variant']
  noWrap?: boolean
  onClick?: () => void
  showNoAddress?: boolean
}

function AddressMapLink(props: AddressMapLinkProps) {
  const {
    address,
    children,
    variant = 'pin-and-title',
    latitude,
    longitude,
    textVariant = 'inherit',
    noWrap = false,
    onClick,
    showNoAddress = false,
    entityId,
    entityType,
    serviceLocationId,
  } = props

  const isGeocoded =
    latitude != null && longitude != null && latitude !== 0 && longitude !== 0
  const mapViewEnabled = useMapViewFeatureEnabled()

  const [canViewLeads, canViewOpps] = useCheckPermissions(
    'leads_view',
    'opportunities_view',
  )

  const theme = useTheme()
  const styles = getStyles(theme)

  const { clear, setAddressLookup } = useData(props)
  const { handleAddressClick } = useHandlers({
    clear,
    setAddressLookup,
    entityId,
    entityType,
    serviceLocationId,
    latitude,
    longitude,
  })

  if (!mapViewEnabled || (!canViewLeads && !canViewOpps))
    return (
      <Box
        sx={{ whiteSpace: noWrap ? 'nowrap' : 'break-spaces' }}
        component={'span'}
      >
        {children}
      </Box>
    )

  if (!address)
    return (
      <Box sx={{ whiteSpace: noWrap ? 'nowrap' : 'break-spaces' }}>
        {showNoAddress ? (
          <>
            <MapAddressIcon disabled={!isGeocoded} />

            <Link
              href="#"
              underline="none"
              color="primary"
              target="_blank"
              rel="noreferrer"
              sx={styles.disabled}
              variant={textVariant}
            >
              No address provided
            </Link>
          </>
        ) : (
          <>{children}</>
        )}
      </Box>
    )

  return (
    <Box
      sx={{ whiteSpace: noWrap ? 'nowrap' : 'break-spaces' }}
      component={'span'}
    >
      {variant === 'pin-and-title' && (
        <>
          <MapAddressIcon disabled={!isGeocoded} sx={{ marginTop: '-2px' }} />
        </>
      )}
      {isGeocoded ? (
        <Link
          href="#"
          underline="none"
          color="primary"
          target="_blank"
          rel="noreferrer"
          onClick={(e) => {
            e.preventDefault()

            if (onClick) {
              onClick()
            } else {
              handleAddressClick(address)
            }
          }}
          sx={styles.active}
          variant={textVariant}
          mt={-2}
        >
          {children}
        </Link>
      ) : (
        <>
          <Link
            href="#"
            underline="none"
            color={theme.palette.text.disabled}
            target="_blank"
            rel="noreferrer"
            sx={styles.disabled}
            variant={textVariant}
          >
            {children}
          </Link>

          <LightTooltip
            sx={{ mt: 1 }}
            title="Location will not display in map view. The address is either invalid or could not be found."
          >
            <InfoOutlined
              width="small"
              sx={styles.infoIcon}
              data-test-id="addressNotMappableInfoIcon"
            />
          </LightTooltip>
        </>
      )}
    </Box>
  )
}

export default AddressMapLink
